import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { ChatMessage, ClientCard } from "../types";
import { Message } from "./Message";
import { InputBox } from "./InputBox";
import { TermsModal } from "./TermsModal";
import { EmailTranscript } from "./EmailTranscript";
import { TERMS_VERSION } from "../terms";

interface Props {
  clientId: string;
  brandName: string;
  welcomeMessage: string;
  starterPrompts: string[];
  messages: ChatMessage[];
  isStreaming: boolean;
  onSubmit: (text: string) => void;
  // Email-transcript feature wiring. ccAvailable comes from /v1/config
  // (only true when the client has admin-configured notification
  // recipients). onSendTranscript proxies to the API; widget never sees
  // the CC addresses.
  emailCcAvailable: boolean;
  onSendTranscript: (
    email: string,
    ccTeam: boolean,
    honeypot: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  cards: ClientCard[];
}

function tcStorageKey(clientId: string): string {
  return `aiw-tc-${clientId}-${TERMS_VERSION}`;
}

function readAcceptedFlag(clientId: string): boolean {
  try {
    // Dev/QA convenience: ?reset-tc in the URL wipes the per-client flag
    // and strips itself from the URL so a fresh acceptance still persists.
    const url = new URL(window.location.href);
    if (url.searchParams.has("reset-tc")) {
      localStorage.removeItem(tcStorageKey(clientId));
      url.searchParams.delete("reset-tc");
      window.history.replaceState({}, "", url.toString());
      return false;
    }
    return localStorage.getItem(tcStorageKey(clientId)) === "1";
  } catch {
    // localStorage can throw in sandboxed iframes or with strict
    // browser settings; fall through to in-memory state and re-prompt.
    return false;
  }
}

function writeAcceptedFlag(clientId: string): void {
  try {
    localStorage.setItem(tcStorageKey(clientId), "1");
  } catch {
    // Silently tolerate; the in-memory state still gates the session.
  }
}

export function Panel({
  clientId,
  brandName,
  welcomeMessage,
  starterPrompts,
  messages,
  isStreaming,
  onSubmit,
  emailCcAvailable,
  onSendTranscript,
  cards,
}: Props) {
  const messagesRef = useRef<HTMLDivElement>(null);
  const prevMessageCount = useRef(0);

  // Hide starter prompts behind a toggle in the welcome state so a
  // permanently-visible panel doesn't feel busy.
  const [startersVisible, setStartersVisible] = useState(false);

  const [tcAccepted, setTcAccepted] = useState<boolean>(() =>
    readAcceptedFlag(clientId),
  );

  // Bottom padding equal to the messages container's visible height. This
  // creates enough scrollable headroom under the latest message that
  // scrollIntoView({block:"start"}) below can always actually reach the
  // top — even when the streaming response below the user's question is
  // still short. The padding is invisible scrollable space; users can
  // scroll back through it. Recomputed whenever a new turn starts so the
  // headroom reflects the current container size.
  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    if (messages.length === 0) {
      el.style.paddingBottom = "";
    } else {
      el.style.paddingBottom = `${el.clientHeight}px`;
    }
  }, [messages.length]);

  // Scroll only when a new turn begins (messages.length increased), not on
  // every streaming-delta content update. Pinning the user's question to
  // the top of the messages area lets them read the answer from its first
  // word as it streams in, instead of being dragged down to follow the
  // cursor. Pattern from ChatGPT / Claude.ai.
  //
  // `scrollIntoView` is intentionally NOT used here: it walks up through
  // every scrollable ancestor and scrolls them all until the target is
  // "in view." For a widget embedded inside a tall host page (e.g., the
  // Elementor page on Ward's site), that drags the entire page along with
  // the chat. Computing the delta with getBoundingClientRect and writing
  // straight to messagesRef.scrollTop keeps the scroll contained inside
  // the messages container.
  const TOP_OFFSET_PX = 12;
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    if (messages.length > prevMessageCount.current) {
      const userMsgs = el.querySelectorAll<HTMLElement>(".message.user");
      const last = userMsgs[userMsgs.length - 1];
      if (last) {
        // On a phone the host page is scrolled down when the input gains focus
        // (the keyboard reveals it mid-screen). The desktop scroll below is
        // intentionally contained to the messages container and never moves
        // the page, so the new question/answer would stay out of view. On
        // phones only, use scrollIntoView — it walks up to the page and brings
        // the question (with the streaming answer below it) back into view.
        // Scoped to touch + narrow viewport so larger screens keep the tuned,
        // page-stable behavior untouched. scroll-margin-top on .message.user
        // gives the same 12px gap as TOP_OFFSET_PX.
        const isPhone =
          typeof window !== "undefined" &&
          window.matchMedia?.("(pointer: coarse) and (max-width: 768px)")
            .matches;
        if (isPhone) {
          last.scrollIntoView({ block: "start", behavior: "smooth" });
        } else {
          const elRect = el.getBoundingClientRect();
          const userRect = last.getBoundingClientRect();
          const target =
            el.scrollTop + (userRect.top - elRect.top) - TOP_OFFSET_PX;
          el.scrollTo({ top: target, behavior: "smooth" });
        }
      } else {
        el.scrollTop = el.scrollHeight;
      }
    }
    prevMessageCount.current = messages.length;
  }, [messages]);

  const isEmpty = messages.length === 0;

  return (
    <div class={`panel${tcAccepted && isEmpty ? " empty" : ""}`}>
      {!tcAccepted ? (
        <div class="terms-host">
          <TermsModal
            clientId={clientId}
            brandName={brandName}
            onAccept={() => {
              writeAcceptedFlag(clientId);
              setTcAccepted(true);
            }}
          />
        </div>
      ) : (
        <>
          <div class="messages" ref={messagesRef}>
            {isEmpty ? (
              <div class="welcome">
                {welcomeMessage && (
                  <div class="welcome-text">{welcomeMessage}</div>
                )}
                {starterPrompts.length > 0 &&
                  (startersVisible ? (
                    <div class="starter-prompts">
                      {starterPrompts.map((p) => (
                        <button
                          key={p}
                          class="starter-prompt"
                          onClick={() => onSubmit(p)}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <button
                      class="starter-toggle"
                      onClick={() => setStartersVisible(true)}
                    >
                      See suggested searches
                    </button>
                  ))}
              </div>
            ) : (
              messages.map((m, i) => (
                <Message key={i} message={m} cards={cards} />
              ))
            )}
          </div>

          {!isEmpty && (
            <EmailTranscript
              ccAvailable={emailCcAvailable}
              brandName={brandName}
              onSend={onSendTranscript}
            />
          )}

          <InputBox
            onSubmit={onSubmit}
            disabled={isStreaming}
            placeholder={isStreaming ? "Waiting for response..." : undefined}
          />
        </>
      )}

      <div class="footer">
        Powered by{" "}
        <a
          href="https://lavanewmedia.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Lava
        </a>
      </div>
    </div>
  );
}
